import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, MessageCircle, Paperclip, StickyNote, Bot } from 'lucide-react';
import { toast } from 'sonner';
import { api, errText } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loading, KV, fmtDate } from '@/components/OpsUI';
import { MessageBubble } from '@/components/whatsapp/MessageBubble';
import { MediaAttachment } from '@/components/whatsapp/MediaAttachment';
import { WaTicketControls } from './WaTicketControls';
import { WaTicketReply } from './WaTicketReply';

export function WaTicketDetail({ id, canAct, onClose, onChange }) {
  const [t, setT] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const load = useCallback(() => {
    if (!id) return;
    api.get(`/wa-tickets/${id}`).then(({ data }) => setT(data)).catch((e) => { toast.error(errText(e)); closeRef.current(); });
  }, [id]);
  useEffect(() => { setT(null); load(); }, [load]);
  const refresh = () => { load(); onChange(); };
  const r = t?.requester || {};

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="wa-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <Ticket className="w-5 h-5 text-emerald-400" />
            {t ? <><span className="font-mono text-emerald-300" data-testid="wa-ticket-dialog-number">{t.number}</span> · {t.subject}</> : 'Ticket'}
          </DialogTitle>
        </DialogHeader>
        {!t ? <div className="p-8"><Loading /></div> : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6" data-testid="wa-ticket-requester">
              <KV k="Solicitante" v={r.name || '—'} />
              <KV k="Iglesia" v={r.church || '—'} />
              <KV k="Teléfono" v={r.phone} />
              <KV k="Correo" v={r.email || '—'} />
              <KV k="Plataforma" v={t.platform} />
              <KV k="Creado" v={`${fmtDate(t.created_at)} · ${t.created_by === 'ai' ? 'Agente IA' : t.created_by}`} />
            </div>
            {t.created_by === 'ai' && <p className="text-xs flex items-center gap-1.5 text-[hsl(var(--primary))]"><Bot className="w-3.5 h-3.5" />El agente IA detectó que este caso necesitaba al equipo técnico y abrió el ticket automáticamente.</p>}
            {t.notice_error && <p className="text-xs text-amber-300" data-testid="wa-ticket-notice-error">Aviso al usuario no enviado: {t.notice_error}</p>}
            <WaTicketControls t={t} canAct={canAct} onChange={refresh} />
            <div>
              <h3 className="text-sm font-semibold mb-2">Resumen del caso</h3>
              <div className="rounded-lg border border-border bg-[hsl(var(--muted))]/20 p-3 text-sm whitespace-pre-wrap" data-testid="wa-ticket-description">{t.description}</div>
            </div>
            {t.attachments?.length > 0 && (
              <div data-testid="wa-ticket-attachments">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Paperclip className="w-4 h-4" />Adjuntos ({t.attachments.length})</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {t.attachments.map((a) => <MediaAttachment key={`${a.msg_id}-${a.idx}`} msgId={a.msg_id} idx={a.idx} contentType={a.content_type} />)}
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-emerald-400" />Conversación de WhatsApp</h3>
                <Button asChild size="sm" variant="outline" className="h-7 text-xs"><Link to={`/whatsapp?c=${t.conversation_id}`} data-testid="wa-ticket-open-conversation">Abrir chat</Link></Button>
              </div>
              <div className="space-y-3 max-h-[380px] overflow-y-auto rounded-lg p-3 bg-[hsl(var(--muted)/.15)]" data-testid="wa-ticket-transcript">
                {t.messages.map((m) => <MessageBubble key={m.id} m={m} />)}
              </div>
            </div>
            {t.notes?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-1.5"><StickyNote className="w-4 h-4 text-amber-400" />Notas internas</h3>
                {t.notes.map((n, i) => (
                  <div key={i} className="rounded-lg p-3 border border-amber-500/30 bg-amber-500/10 text-sm" data-testid={`wa-ticket-note-${i}`}>
                    <div className="flex justify-between text-xs mb-1"><span className="font-medium">{n.agent_name}</span><span>{fmtDate(n.timestamp)}</span></div>
                    <div className="whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
              </div>
            )}
            {canAct && <WaTicketReply t={t} onDone={refresh} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
